import { MouseEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../core/context/app.context';
import { Util } from '../../utils/common.utils';
import { RadialBackgroundType } from '../background/radial-gradient';
import styles from './header.module.scss';

export const Header = ({
  activeStep,
  navigate,
  backgroundType,
  headerTitle,
  headerConfig,
}: {
  activeStep: number;
  navigate: {
    canNavigate: boolean;
    navigateToPrevious: boolean;
    disableActions?: boolean;
    hideProgress?: boolean;
    displayBackButton?: boolean;
  };
  backgroundType: RadialBackgroundType;
  headerTitle?: string;
  headerConfig: any;
}) => {
  const { navigateToStreamLanding, navigateToFlexLanding, activeTheme } = useContext(AppContext);
  const totalSteps = 4;
  const { t } = useTranslation();

  const handleBackClick = (e: MouseEvent) => {
    e.preventDefault();
    if (navigate.navigateToPrevious) {
      window.history.go(-1);
      return;
    }

    redirect();
  };

  const getHeaderLogo = () => {
    if (headerConfig.isSelect) {
      return Util.GetImageAsset('dstv-flex-logo.png');
    }

    if (headerConfig.isCommon) {
      return Util.GetImageAsset('dstv-header-logo.png');
    }

    return getThemeLogo();
  };

  const getThemeLogo = () => {
    if (activeTheme == 'light') {
      return Util.GetImageAsset('dstv-logo-light.png');
    }

    return Util.GetImageAsset('dstv-logo.png');
  };

  const redirect = () => {
    if (headerConfig.isSelect) {
      navigateToFlexLanding();
      return;
    }

    navigateToStreamLanding();
  };

  const logoStyling = () => {
    if (headerConfig.isSelect) {
      return styles.flexLogo;
    }

    return styles.logo;
  };

  const isFinalStep = () => {
    return activeStep === totalSteps;
  };

  const navConfig = () => {
    if (headerConfig.isCommon) {
      return headerConfig.isCommon;
    }

    return !navigate.disableActions || navigate.displayBackButton;
  };

  return (
    <>
      <nav className={`${styles.navBar}`}>
        <div className={`container ${styles.headerContent} d-flex justify-content-between`}>
          <div>
            {navigate.canNavigate && navConfig() && (
              <a onClick={handleBackClick} className={`nav-link ${styles.backButton}`} href="/#">
                <h5 className={styles.backText}>
                  <i className="icon-Arrow_left_thin"></i>
                  <span className={styles.backTextStyling}>{t('header.backNavigation')}</span>
                </h5>
              </a>
            )}
          </div>
          {!headerTitle ? (
            <div className={logoStyling()}>
              <img src={getHeaderLogo()} alt="DStv Logo" />
            </div>
          ) : (
            <div className="d-flex justify-content-center align-items-center">
              <h4 className="text-center">{headerTitle}</h4>
            </div>
          )}
          <div className={styles.activeStep}>
            {!navigate.disableActions && (
              <h5>
                {isFinalStep() ? (
                  <span className={styles.stepText}>{t('header.finalStep')}</span>
                ) : (
                  <>
                    <span className={styles.stepText}>{t('header.step')}</span>
                    {`${activeStep} / `}
                    <span className={styles.stepText}>{totalSteps}</span>
                  </>
                )}
              </h5>
            )}
          </div>
        </div>
      </nav>
      {!navigate.hideProgress && (
        <div className={`progress ${styles.navProgress}`}>
          <div
            className={`progress-bar ${styles.progressBar} ${styles[backgroundType]}`}
            role="progressbar"
            style={{
              width: `${activeStep * 25}%`,
            }}
            aria-valuenow={0}
            aria-valuemin={0}
            aria-valuemax={100}
          ></div>
        </div>
      )}
    </>
  );
};
